import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// API URL - handle custom domains
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  if (typeof window !== 'undefined') {
    const isCustomDomain = !window.location.hostname.includes('preview.emergentagent.com');
    const envPointsToPreview = envUrl?.includes('preview.emergentagent.com');
    
    if (isCustomDomain && envPointsToPreview) {
      return window.location.origin;
    }
  }
  
  return envUrl;
};

const API = getApiUrl();

// Utility functions for handling state changes (outside component to avoid re-creation)
const showStateChangeNotification = (exam, fromStatus, toStatus) => {
  console.log(`[ExamPolling] State change: ${exam.title} ${fromStatus} -> ${toStatus}`);
  
  if (toStatus === 'active' && fromStatus === 'draft') {
    // Exam just started!
    toast.success(`🚀 O exame "${exam.title}" começou!`, {
      description: `Tem ${exam.duration_minutes} minutos para completar.`,
      duration: 10000,
      action: {
        label: 'Iniciar',
        onClick: () => window.location.href = `/exam/${exam.exam_id}`
      }
    });
    
    // Play notification sound if available
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {}); // Ignore if blocked
    } catch (e) {
      // Ignore audio errors
    }
  }
  
  if (toStatus === 'completed' && fromStatus === 'active') {
    // Exam just ended
    toast.info(`⏱️ O exame "${exam.title}" terminou.`, {
      description: 'O tempo acabou.',
      duration: 8000
    });
  }
};

// Handle review unlock notification (Issue 3 fix)
const showReviewUnlockedNotification = (exam) => {
  console.log(`[ExamPolling] Review unlocked: ${exam.title}`);
  
  const resultText = exam.result?.passed 
    ? `Parabéns! Passou com ${exam.result?.accuracy}%.` 
    : `Resultado: ${exam.result?.accuracy}%. Continue a estudar!`;
  
  toast.success(`📋 Resultado disponível: "${exam.title}"`, {
    description: resultText,
    duration: 10000,
    action: {
      label: 'Ver Resultado',
      onClick: () => window.location.href = `/exam/${exam.exam_id}/review`
    }
  });
  
  // Play notification sound
  try {
    const audio = new Audio('/notification.mp3');
    audio.volume = 0.5;
    audio.play().catch(() => {});
  } catch (e) {
    // Ignore audio errors
  }
};

/**
 * Hook for real-time exam state polling
 * Polls the server every `interval` ms and notifies on state changes
 * 
 * @param {number} interval - Polling interval in milliseconds (default: 3000)
 * @param {boolean} enabled - Whether polling is enabled (default: true)
 * @returns {Object} - { exams, loading, error, lastUpdate, refetch }
 */
export function useExamPolling(interval = 3000, enabled = true) {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Store previous exam states to detect changes
  const prevExamsRef = useRef({});
  const pollingRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchExamStates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/exams/poll`, {
        withCredentials: true
      });
      
      if (!mountedRef.current) return;
      
      const newExams = response.data.exams || [];
      setExams(newExams);
      setLastUpdate(response.data.server_timestamp);
      setError(null);
      setLoading(false);
      
      // Check for state changes and notify
      newExams.forEach(exam => {
        const prevState = prevExamsRef.current[exam.exam_id];
        
        if (prevState) {
          // Detect status transitions
          if (prevState.status !== exam.status) {
            showStateChangeNotification(exam, prevState.status, exam.status);
          }
          
          // Detect review unlock (Issue 3 fix)
          if (exam.completed && exam.result?.review_unlocked && !prevState.result?.review_unlocked) {
            showReviewUnlockedNotification(exam);
          }
        }
        
        // Update previous state
        prevExamsRef.current[exam.exam_id] = { ...exam };
      });
      
    } catch (err) {
      if (!mountedRef.current) return;
      
      // Don't show error for auth issues (user might have logged out)
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError(err.message);
      }
      setLoading(false);
    }
  }, []);

  const refetch = useCallback(() => {
    fetchExamStates();
  }, [fetchExamStates]);

  useEffect(() => {
    mountedRef.current = true;
    
    if (enabled) {
      // Initial fetch
      fetchExamStates();
      
      // Start polling
      pollingRef.current = setInterval(fetchExamStates, interval);
    }
    
    return () => {
      mountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [enabled, interval, fetchExamStates]);

  return {
    exams,
    loading,
    error,
    lastUpdate,
    refetch
  };
}

/**
 * Hook for monitoring a specific exam's state in real-time
 * More frequent polling for active exams
 * 
 * @param {string} examId - The exam ID to monitor
 * @param {boolean} enabled - Whether polling is enabled
 * @returns {Object} - { exam, timeRemaining, serverTimestamp, loading, error }
 */
export function useExamMonitor(examId, enabled = true) {
  const [exam, setExam] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [serverTimestamp, setServerTimestamp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const pollingRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchExamState = useCallback(async () => {
    if (!examId) return;
    
    try {
      const response = await axios.get(`${API}/api/exams/poll`, {
        withCredentials: true
      });
      
      if (!mountedRef.current) return;
      
      const examData = response.data.exams?.find(e => e.exam_id === examId);
      
      if (examData) {
        setExam(examData);
        setTimeRemaining(examData.time_remaining_seconds);
        setServerTimestamp(response.data.server_timestamp);
      }
      
      setError(null);
      setLoading(false);
      
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err.message);
      setLoading(false);
    }
  }, [examId]);

  useEffect(() => {
    mountedRef.current = true;
    
    if (enabled && examId) {
      // Initial fetch
      fetchExamState();
      
      // Poll more frequently for exam monitoring (every 2 seconds)
      pollingRef.current = setInterval(fetchExamState, 2000);
    }
    
    return () => {
      mountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [enabled, examId, fetchExamState]);

  return {
    exam,
    timeRemaining,
    serverTimestamp,
    loading,
    error,
    refetch: fetchExamState
  };
}

export default useExamPolling;
